// Knowledge-base search (design §6.1): a search field and results with the
// ts_headline fragment. The fragment's `«…»` marks are turned into
// <mark> elements by splitting the string — nothing is ever injected as
// HTML, so page text containing markup stays inert text.

import type { ReactNode } from 'react'
import type { KbSearchHit, KbSpaceLabel } from './kb-types'

export interface KbSearchProps {
  /** Controlled query; debounce in the parent before fetching. */
  query: string
  onQueryChange: (query: string) => void
  /** null = no search yet (hides the results list). */
  results: KbSearchHit[] | null
  spaceLabel: KbSpaceLabel
  loading?: boolean
  error?: string | null
  onSelect: (hit: KbSearchHit) => void
  placeholder?: string
  autoFocus?: boolean
}

export function KbSearch({
  query,
  onQueryChange,
  results,
  spaceLabel,
  loading,
  error,
  onSelect,
  placeholder = 'Search the knowledge base…',
  autoFocus,
}: KbSearchProps) {
  return (
    <div className="min-w-0">
      <input
        type="search"
        className="field-input"
        value={query}
        placeholder={placeholder}
        aria-label="Search the knowledge base"
        autoFocus={autoFocus}
        onChange={(event) => onQueryChange(event.target.value)}
      />
      {error ? (
        <p className="mt-2 text-sm text-danger">{error}</p>
      ) : loading ? (
        <p className="mt-2 text-sm text-ink-3">Searching…</p>
      ) : results ? (
        results.length === 0 ? (
          <p className="mt-2 text-sm text-ink-3">No pages match “{query}”.</p>
        ) : (
          <ul className="mt-2 divide-y divide-line rounded-panel border border-line bg-surface">
            {results.map((hit) => (
              <li key={`${hit.space}|${hit.path}`}>
                <button
                  type="button"
                  onClick={() => onSelect(hit)}
                  className="block w-full px-4 py-2.5 text-left hover:bg-sunken/60"
                >
                  <span className="flex items-center gap-2 text-base font-medium text-ink">
                    {hit.title}
                    {hit.stale ? (
                      <span className="rounded-full bg-signal-soft px-2 text-xs font-medium text-signal">
                        may be out of date
                      </span>
                    ) : null}
                  </span>
                  <span className="block text-xs text-ink-3">
                    {spaceLabel(hit.space)} / <span className="font-mono">{hit.path}</span>
                  </span>
                  {hit.snippet ? (
                    <span className="mt-1 block text-sm text-ink-2">
                      <Highlighted text={hit.snippet} />
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  )
}

/**
 * Renders a search snippet: text between `«` and `»` is highlighted, every
 * other character (including anything that looks like markup) is plain text.
 */
export function Highlighted({ text }: { text: string }) {
  const parts: ReactNode[] = []
  let rest = text
  let key = 0
  while (rest) {
    const open = rest.indexOf('«')
    if (open === -1) {
      parts.push(rest)
      break
    }
    if (open > 0) parts.push(rest.slice(0, open))
    const close = rest.indexOf('»', open + 1)
    const end = close === -1 ? rest.length : close
    parts.push(
      <mark key={key++} className="rounded-sm bg-signal-soft px-0.5 font-medium text-ink">
        {rest.slice(open + 1, end)}
      </mark>
    )
    rest = close === -1 ? '' : rest.slice(close + 1)
  }
  return <>{parts}</>
}
